/**
 * @quotech/auth - Legacy Auth Bridge
 * 
 * DUAL-READ IMPLEMENTATION
 * 
 * This module reads from ALL legacy auth storage keys across Quotech modules
 * to ensure seamless migration without logging users out.
 * 
 * Legacy keys by module:
 * - Dashboard: IdToken, refreshToken, UserData
 * - CRM: quotech-crm-auth (Zustand persist)
 * - QA: qa-auth-storage (Zustand persist)
 * - QDrive: qdrive-auth-storage (Zustand persist)
 * - Workforce: workforce-hub-auth (Zustand persist)
 * - CFO: quotech_cfo_token, quotech_cfo_org
 * - Billing: billing-auth-storage (Zustand persist)
 * - Email: email-hub-auth (Zustand persist)
 * - 0x3admin: quotech-admin-auth (Zustand persist)
 */

import type { User, TokenPair, LegacyKeyConfig } from './types'
import { isTokenExpired } from './cookie-auth/tokenValidation'

/**
 * All legacy storage key configurations
 */
export const LEGACY_CONFIGS: LegacyKeyConfig[] = [
  // Dashboard (Redux-based)
  {
    module: 'dashboard',
    tokenKeys: ['IdToken', 'accessToken', 'access_token', 'token'],
    userKeys: ['UserData', 'ProfileData'],
    orgIdKey: 'org_id',
    orgNameKey: 'org_name',
    isZustandPersist: false,
  },
  // CRM
  {
    module: 'crm',
    tokenKeys: ['quotech-crm-auth'],
    userKeys: ['quotech-crm-auth'],
    isZustandPersist: true,
  },
  // QA
  {
    module: 'qa',
    tokenKeys: ['qa-auth-storage'],
    userKeys: ['qa-auth-storage'],
    isZustandPersist: true,
  },
  // QDrive
  {
    module: 'qdrive',
    tokenKeys: ['qdrive-auth-storage'],
    userKeys: ['qdrive-auth-storage'],
    isZustandPersist: true,
  },
  // Workforce
  {
    module: 'workforce',
    tokenKeys: ['workforce-hub-auth'],
    userKeys: ['workforce-hub-auth'],
    isZustandPersist: true,
  },
  // CFO (custom keys)
  {
    module: 'cfo',
    tokenKeys: ['quotech_cfo_token'],
    userKeys: [],
    orgIdKey: 'quotech_cfo_org',
    isZustandPersist: false,
  },
  // Billing
  {
    module: 'billing',
    tokenKeys: ['billing-auth-storage'],
    userKeys: ['billing-auth-storage'],
    isZustandPersist: true,
  },
  // Email Hub
  {
    module: 'email',
    tokenKeys: ['email-hub-auth'],
    userKeys: ['email-hub-auth'],
    isZustandPersist: true,
  },
  // 0x3admin
  {
    module: 'admin',
    tokenKeys: ['quotech-admin-auth'],
    userKeys: ['quotech-admin-auth'],
    isZustandPersist: true,
  },
]

/**
 * Parse a Zustand persisted state from localStorage
 */
function parseZustandState(key: string): { state?: Record<string, unknown> } | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // Ensure we return a valid object with state property
    if (parsed && typeof parsed === 'object') {
      return parsed
    }
    return null
  } catch {
    return null
  }
}

/**
 * Extract tokens from a Zustand persisted state
 */
function extractTokensFromZustand(state: Record<string, unknown>): TokenPair | null {
  // Common patterns in Quotech Zustand stores
  const accessToken = 
    state.accessToken as string | undefined ||
    state.access as string | undefined ||
    state.token as string | undefined ||
    (state.state as Record<string, unknown>)?.accessToken as string | undefined

  const refreshToken =
    state.refreshToken as string | undefined ||
    state.refresh as string | undefined ||
    (state.state as Record<string, unknown>)?.refreshToken as string | undefined

  if (accessToken) {
    return {
      access: accessToken,
      refresh: refreshToken || '',
    }
  }
  return null
}

/**
 * Extract user from a Zustand persisted state
 */
function extractUserFromZustand(state: Record<string, unknown>): User | null {
  // Look for user object in various locations
  const user = 
    state.user as Record<string, unknown> | undefined ||
    (state.state as Record<string, unknown>)?.user as Record<string, unknown> | undefined

  if (user && (user.email || user.id)) {
    return {
      id: (user.id as string) || '',
      email: (user.email as string) || '',
      name: (user.name as string) || (user.full_name as string) || (user.first_name as string) || '',
      first_name: user.first_name as string | undefined,
      last_name: user.last_name as string | undefined,
      full_name: user.full_name as string | undefined,
      organisation_id: (user.organisation_id as string) || (user.org_id as string) || '',
      organisation_name: (user.organisation_name as string) || (user.org_name as string) || '',
      role: user.role as string | undefined,
      permissions: user.permissions as string[] | undefined,
      avatar_url: user.avatar_url as string | undefined,
      phone: user.phone as string | undefined,
    }
  }
  return null
}

/**
 * Try to read auth from a specific legacy config
 */
function tryReadLegacyConfig(config: LegacyKeyConfig): {
  tokens: TokenPair | null
  user: User | null
  orgId: string | null
  orgName: string | null
} {
  let tokens: TokenPair | null = null
  let user: User | null = null
  let orgId: string | null = null
  let orgName: string | null = null

  if (config.isZustandPersist) {
    // Zustand persisted store
    for (const key of config.tokenKeys) {
      const parsed = parseZustandState(key)
      // Safely access state with null checks
      const state = parsed?.state
      if (state && typeof state === 'object') {
        const stateObj = state as Record<string, unknown>
        if (!tokens) {
          tokens = extractTokensFromZustand(stateObj)
        }
        if (!user) {
          user = extractUserFromZustand(stateObj)
        }
        if (user) {
          orgId = user.organisation_id || null
          orgName = user.organisation_name || null
        }
        if (tokens) break
      }
    }
  } else {
    // Direct localStorage keys
    for (const key of config.tokenKeys) {
      try {
        const value = localStorage.getItem(key)
        if (value) {
          // Try to parse as JSON first
          try {
            const parsed = JSON.parse(value)
            if (typeof parsed === 'object' && parsed.access) {
              tokens = { access: parsed.access, refresh: parsed.refresh || '' }
            }
          } catch {
            // Not JSON, use as raw token
            tokens = { access: value, refresh: '' }
          }
          if (tokens) break
        }
      } catch {
        continue
      }
    }

    for (const key of config.userKeys) {
      try {
        const value = localStorage.getItem(key)
        if (value) {
          const parsed = JSON.parse(value)
          if (parsed && (parsed.email || parsed.id)) {
            user = {
              id: parsed.id || '',
              email: parsed.email || '',
              name: parsed.name || parsed.full_name || parsed.first_name || '',
              first_name: parsed.first_name,
              last_name: parsed.last_name,
              full_name: parsed.full_name,
              organisation_id: parsed.organisation_id || parsed.org_id || '',
              organisation_name: parsed.organisation_name || parsed.org_name || '',
              role: parsed.role,
              permissions: parsed.permissions,
              avatar_url: parsed.avatar_url,
              phone: parsed.phone,
            }
            break
          }
        }
      } catch {
        continue
      }
    }

    // Org ID from separate key
    if (config.orgIdKey) {
      orgId = localStorage.getItem(config.orgIdKey)
    }
    if (config.orgNameKey) {
      orgName = localStorage.getItem(config.orgNameKey)
    }
  }

  return { tokens, user, orgId, orgName }
}

export interface MigratedAuth {
  tokens: TokenPair
  user: User | null
  orgId: string | null
  orgName: string | null
  source: string
}

/**
 * Try to migrate auth from any legacy storage.
 * Returns the first valid, LIVE auth found.
 *
 * DURABILITY (BUG-2026-06-30-STALE-TOKEN-RESURRECTION — root of the
 * STALE-COOKIE-JAR-LOGOUT recurrence family): a candidate only counts as
 * valid when its access token is a live (non-expired) JWT. Without this
 * gate a DEAD legacy bearer — e.g. a previous user's expired `IdToken` /
 * `ProfileData` left in localStorage — is resurrected on every boot: the
 * two callers (`authStore.onRehydrateStorage` and
 * `authActions.performCheckAuth`) write it straight back into the shared
 * `.quotech.ai` cookie jar and flip `isAuthenticated = true`. The app then
 * fires authed requests against a dead identity (the operator's "401 storm
 * on every refresh") and SessionManager kills the resurrected session
 * mid-login (`/login?reason=invalid` → bounce-breaker gates the manual
 * sign-in). `isTokenExpired` returns true for BOTH an expired JWT and any
 * unparseable/opaque token, so one gate closes both shapes. The genuine
 * "stay logged in" path is unaffected — it rides the HttpOnly
 * `quotech_refresh` cookie via SessionManager's silent refresh, never this
 * localStorage migration. Skipping an expired candidate continues the scan
 * so a still-live token in another hub store can still migrate.
 */
export function migrateLegacyAuth(): MigratedAuth | null {
  for (const config of LEGACY_CONFIGS) {
    const result = tryReadLegacyConfig(config)
    if (result.tokens && !isTokenExpired(result.tokens.access)) {
      return {
        tokens: result.tokens,
        user: result.user,
        orgId: result.orgId,
        orgName: result.orgName,
        source: config.module,
      }
    }
  }
  return null
}

/**
 * Check if user has any valid legacy auth
 */
export function hasLegacyAuth(): boolean {
  return migrateLegacyAuth() !== null
}

/**
 * Clear all legacy auth keys (call after successful migration)
 * NOTE: Only call this after a deprecation period!
 */
export function clearLegacyAuth(): void {
  const keysToRemove = [
    // Dashboard
    'IdToken', 'refreshToken', 'accessToken', 'access_token', 'token',
    'UserData', 'ProfileData', 'org_id', 'org_name',
    // Zustand stores
    'quotech-crm-auth',
    'qa-auth-storage',
    'qdrive-auth-storage',
    'workforce-hub-auth',
    'billing-auth-storage',
    'email-hub-auth',
    'quotech-admin-auth',
    // CFO
    'quotech_cfo_token', 'quotech_cfo_org',
  ]

  for (const key of keysToRemove) {
    try {
      localStorage.removeItem(key)
    } catch {
      // Ignore errors
    }
  }
}

